import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, Clock } from 'lucide-react';
import { BLOG_POSTS, VIDEOS, VIDEO_META } from '../data/mock';
import { usePageMeta } from '../components/Layout';
import { Reveal, SectionHeader, PageHero, CloseCTA, JsonLd, FAQAccordion, PitVideo } from '../components/Shared';

const SITE = 'https://pitfitworkout.com';

/* Parse [text](url) into React links — internal via router, external via <a> */
function renderInline(text) {
  const re = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let last = 0;
  let m;
  let k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const label = m[1];
    const url = m[2];
    if (url.startsWith('/')) {
      parts.push(<Link key={k++} to={url} className="underline underline-offset-4 hover:text-white transition-colors" style={{ color: 'var(--pf-brand2)' }}>{label}</Link>);
    } else {
      parts.push(<a key={k++} href={url} target="_blank" rel="noreferrer noopener" className="underline underline-offset-4" style={{ color: 'var(--pf-brand2)' }}>{label}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function ArticleBlock({ block }) {
  if (typeof block === 'string') {
    if (block.startsWith('## ')) {
      return <h2 className="font-display font-bold text-white text-2xl mt-10 mb-5">{block.slice(3)}</h2>;
    }
    return <p className="text-[#c3cbdc] text-[17px] leading-[1.8] mb-6">{renderInline(block)}</p>;
  }
  switch (block.type) {
    case 'tldr':
      return (
        <div className="glass-card p-6 md:p-7 mb-8" style={{ borderColor: 'rgba(125,167,255,0.4)', boxShadow: '0 0 24px rgba(70,127,247,0.1)' }}>
          <p className="mono-label mb-3">Short Answer</p>
          <p className="text-white text-[17px] leading-relaxed font-medium">{renderInline(block.text)}</p>
        </div>
      );
    case 'takeaways':
      return (
        <div className="glass-card p-6 md:p-7 my-8">
          <p className="mono-label mb-4">Key Takeaways</p>
          <ul className="space-y-3">
            {block.items.map((it) => (
              <li key={it} className="flex gap-3 text-[15.5px] text-[#c3cbdc] leading-relaxed">
                <span className="mt-[9px] shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: 'var(--pf-brand)' }} />
                {renderInline(it)}
              </li>
            ))}
          </ul>
        </div>
      );
    case 'table':
      return (
        <div className="my-8 overflow-x-auto">
          {block.title && <p className="mono-label mb-3">{block.title}</p>}
          <table className="w-full text-left border border-[rgba(148,170,220,0.16)] rounded-xl overflow-hidden" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr style={{ background: 'var(--pf-surf2)' }}>
                {block.head.map((h) => (
                  <th key={h} className="font-mono-pf text-[11.5px] uppercase tracking-[0.12em] text-white px-4 py-3.5 border-b border-[rgba(148,170,220,0.16)]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri} style={ri % 2 ? { background: 'rgba(20,26,36,0.5)' } : {}}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={`px-4 py-3.5 text-[14.5px] align-top border-b border-[rgba(148,170,220,0.08)] ${ci === 0 ? 'text-white font-medium' : 'text-muted-pf'}`}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'video': {
      const v = VIDEOS[block.video];
      if (!v) return null;
      return (
        <figure className="my-8">
          <div className="rounded-2xl overflow-hidden border border-[rgba(148,170,220,0.16)]">
            <PitVideo video={v} className="w-full h-[320px] md:h-[400px] object-cover" label={block.caption || 'PITFIT training video'} />
          </div>
          {block.caption && <figcaption className="font-mono-pf text-[12px] uppercase tracking-[0.12em] text-muted-pf mt-3">{block.caption}</figcaption>}
        </figure>
      );
    }
    case 'faq':
      return (
        <div className="my-10">
          <h2 className="font-display font-bold text-white text-2xl mb-6">FAQ</h2>
          <FAQAccordion faqs={block.items} />
        </div>
      );
    default:
      return null;
  }
}

/* ============ /blog ============ */
export function BlogList() {
  usePageMeta(
    'Blog — HIIT, Recovery & Dallas Fitness',
    'The PITFIT blog: HIIT training science, recovery guides, pre-workout nutrition, and Dallas fitness guides — written by the coaches who run the PIT.'
  );

  const [featured, ...rest] = BLOG_POSTS;

  return (
    <>
      <PageHero
        tag="The Resource Engine"
        title="Train smarter between classes."
        sub="HIIT science, recovery playbooks, nutrition that fits real life, and honest guides to training in Dallas — from the coaches on the podium."
      />

      <section className="section-pad !pt-8">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          {/* Featured */}
          <Reveal>
            <Link to={`/blog/${featured.slug}`} className="glass-card glass-card-hover overflow-hidden grid lg:grid-cols-2 mb-10 group">
              <img src={featured.img} alt={featured.title} className="w-full h-72 lg:h-full object-cover" loading="lazy" />
              <div className="p-8 lg:p-10 flex flex-col justify-center">
                <div className="flex items-center gap-4">
                  <span className="mono-label !mb-0">{featured.cat}</span>
                  <span className="font-mono-pf text-[11.5px] text-muted-pf flex items-center gap-1.5"><Clock size={12} /> {featured.read} read</span>
                </div>
                <h2 className="font-display font-bold text-white text-2xl lg:text-3xl mt-4 leading-tight">{featured.title}</h2>
                <p className="text-muted-pf text-[15.5px] mt-4 leading-relaxed">{featured.excerpt}</p>
                <span className="btn-quiet mt-6">Read article <ArrowRight size={15} /></span>
              </div>
            </Link>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={i * 80}>
                <Link to={`/blog/${p.slug}`} className="glass-card glass-card-hover overflow-hidden flex flex-col h-full group">
                  <img src={p.img} alt={p.title} className="w-full h-48 object-cover" loading="lazy" />
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3">
                      <span className="mono-label !mb-0">{p.cat}</span>
                      <span className="font-mono-pf text-[11px] text-muted-pf">{p.read}</span>
                    </div>
                    <h3 className="font-display font-semibold text-white text-lg mt-3 leading-snug flex-1">{p.title}</h3>
                    <p className="text-muted-pf text-[14px] mt-3 leading-relaxed">{p.excerpt}</p>
                    <span className="font-mono-pf text-[11.5px] uppercase tracking-[0.12em] text-muted-pf mt-4">{p.date}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CloseCTA title="Reading burns zero calories." sub="The PIT burns 500–900. Your first class is $15 — no membership, no commitment." />
    </>
  );
}

/* ============ /blog/:slug ============ */
export function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  usePageMeta(post ? post.title : 'Blog', post ? post.excerpt : '');

  if (!post) return <Navigate to="/blog" replace />;

  const others = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  const stripLinks = (t) => t.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1');
  const faqBlock = post.body.find((b) => typeof b === 'object' && b.type === 'faq');
  const videoBlocks = post.body.filter((b) => typeof b === 'object' && b.type === 'video' && VIDEOS[b.video] && VIDEO_META[b.video]);

  const articleLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        headline: post.title,
        description: post.excerpt,
        image: post.img.startsWith('http') ? post.img : `${SITE}${post.img}`,
        datePublished: new Date(post.date).toISOString().slice(0, 10),
        articleSection: post.cat,
        author: { '@type': 'Organization', name: 'PITFIT', url: SITE },
        publisher: { '@type': 'Organization', name: 'PITFIT', url: SITE },
        mainEntityOfPage: `${SITE}/blog/${post.slug}`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE}/blog` },
          { '@type': 'ListItem', position: 3, name: post.title, item: `${SITE}/blog/${post.slug}` },
        ],
      },
      ...videoBlocks.map((b) => ({
        '@type': 'VideoObject',
        name: VIDEO_META[b.video].name,
        description: VIDEO_META[b.video].description,
        thumbnailUrl: `${SITE}${VIDEOS[b.video].poster}`,
        contentUrl: `${SITE}${VIDEOS[b.video].src}`,
        uploadDate: new Date(post.date).toISOString().slice(0, 10),
        publisher: { '@type': 'Organization', name: 'PITFIT', url: SITE },
      })),
      ...(faqBlock
        ? [{
            '@type': 'FAQPage',
            mainEntity: faqBlock.items.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: stripLinks(f.a) },
            })),
          }]
        : []),
    ],
  };

  return (
    <>
      <JsonLd data={articleLd} />
      <article>
        <section className="relative overflow-hidden">
          <img src={post.img} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="relative max-w-3xl mx-auto px-5 lg:px-8 py-28 md:py-36">
            <Reveal>
              <div className="flex items-center gap-4">
                <span className="mono-label !mb-0">{post.cat}</span>
                <span className="font-mono-pf text-[12px] text-[#aeb8cc]">{post.date} · {post.read} read</span>
              </div>
              <h1 className="h-display !text-[clamp(2rem,4.5vw,3.2rem)] text-white mt-5">{post.title}</h1>
            </Reveal>
          </div>
        </section>

        <section className="section-pad !pt-14">
          <div className="max-w-3xl mx-auto px-5 lg:px-8">
            {post.body.map((block, i) => (
              <ArticleBlock key={i} block={block} />
            ))}
            <div className="glass-card p-7 mt-10 flex flex-col sm:flex-row sm:items-center justify-between gap-5" style={{ borderColor: 'rgba(125,167,255,0.35)' }}>
              <div>
                <p className="font-display font-semibold text-white text-lg">Put it into practice.</p>
                <p className="text-muted-pf text-[14.5px] mt-1">Your first PITFIT class is $15 — the fastest way to feel what you just read.</p>
              </div>
              <Link to="/book" className="btn-primary shrink-0">Book a Class <ArrowRight size={16} /></Link>
            </div>
          </div>
        </section>
      </article>

      <section className="section-pad !pt-0">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <SectionHeader tag="Keep Reading" title="More from the PIT" />
          <div className="grid md:grid-cols-3 gap-6">
            {others.map((p, i) => (
              <Reveal key={p.slug} delay={i * 70}>
                <Link to={`/blog/${p.slug}`} className="glass-card glass-card-hover overflow-hidden flex flex-col h-full">
                  <img src={p.img} alt={p.title} className="w-full h-44 object-cover" loading="lazy" />
                  <div className="p-6 flex-1">
                    <span className="mono-label">{p.cat}</span>
                    <h3 className="font-display font-semibold text-white text-[17px] mt-2 leading-snug">{p.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
